import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { ParametrosService } from '../../../services/parametros.service';
import { CadastroService } from '../../../services/cadastro.service';
import { Grupo, Subgrupo } from '../../../models/parametros.model';
import { Fornecedor } from '../../../models/cadastro.model';
import { RelatorioContasAPagarDetalhado } from '../../../models/financeiro.model';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';

@Component({
  selector: 'app-relatorio-contas-a-pagar-detalhado',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-contas-a-pagar-detalhado.component.html',
  styleUrl: './relatorio-contas-a-pagar-detalhado.component.css'
})
export class RelatorioContasAPagarDetalhadoComponent implements OnInit {
  private analyticsService = inject(AnalyticsService);
  private parametrosService = inject(ParametrosService);
  private cadastroService = inject(CadastroService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);

  tipoData = 'PAGAMENTO';
  dataInicial = '';
  dataFinal = '';
  grupoId = 0;
  subgrupoId = 0;
  fornecedorId = 0;

  grupos: Grupo[] = [];
  subgrupos: Subgrupo[] = [];
  subgruposFiltrados: Subgrupo[] = [];
  fornecedores: Fornecedor[] = [];

  relatorio: RelatorioContasAPagarDetalhado | null = null;
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  get dataInicialInvalida(): boolean {
    return this.tentouBuscar && this.dataInicial.trim() === '';
  }

  get dataFinalInvalida(): boolean {
    return this.tentouBuscar && this.dataFinal.trim() === '';
  }

  get totalValorPago(): number {
    return (this.relatorio?.itens ?? []).reduce((acc, i) => acc + (i.valorPago ?? 0), 0);
  }

  ngOnInit(): void {
    this.parametrosService.getGrupos().subscribe({ next: data => { this.grupos = data; } });
    this.parametrosService.getSubgrupos().subscribe({ next: data => { this.subgrupos = data; this.subgruposFiltrados = data; } });
    this.cadastroService.getFornecedores().subscribe({ next: data => { this.fornecedores = data; } });
  }

  onGrupoChange(): void {
    this.subgrupoId = 0;
    this.subgruposFiltrados = this.grupoId > 0
      ? this.subgrupos.filter(s => s.grupo?.id === Number(this.grupoId))
      : this.subgrupos;
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.dataInicial.trim() === '' || this.dataFinal.trim() === '') {
      this.toastr.warning('Informe a data inicial e a data final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    const porPagamento = this.tipoData === 'PAGAMENTO';

    this.analyticsService.getRelatorioContasAPagarDetalhado(
      porPagamento ? undefined : this.dataInicial,
      porPagamento ? undefined : this.dataFinal,
      porPagamento ? this.dataInicial : undefined,
      porPagamento ? this.dataFinal : undefined,
      this.grupoId > 0 ? this.grupoId : undefined,
      this.subgrupoId > 0 ? this.subgrupoId : undefined,
      this.fornecedorId > 0 ? this.fornecedorId : undefined
    ).subscribe({
      next: (data) => {
        this.relatorio = data;
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
      },
      error: () => {
        this.toastr.error('Erro ao carregar relatório de contas a pagar.', 'Erro');
        this.carregando = false;
      }
    });
  }

  limpar(): void {
    this.tipoData = 'PAGAMENTO';
    this.dataInicial = '';
    this.dataFinal = '';
    this.grupoId = 0;
    this.subgrupoId = 0;
    this.fornecedorId = 0;
    this.subgruposFiltrados = this.subgrupos;
    this.relatorio = null;
    this.carregado = false;
    this.tentouBuscar = false;
  }

  imprimir(): void {
    if (!this.relatorio) return;

    const brl = (v: number) => formatarMoedaBRL(v ?? 0);

    const esc = (s: string | undefined | null) =>
      (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const tipoLabel = this.tipoData === 'PAGAMENTO' ? 'Pagamento' : 'Vencimento';
    const periodLabel = `${this.formatDate(this.dataInicial)} a ${this.formatDate(this.dataFinal)}`;

    const rows = (this.relatorio.itens ?? []).map((item, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(item.id +'')}</td>
        <td>${esc(item.fornecedor)}</td>
        <td>${esc(item.grupo)}</td>
        <td>${esc(item.subgrupo)}</td>
        <td>${esc(item.descricao)}</td>
        <td>${esc(item.destino)}</td>
        <td class="center">${this.formatDate(item.dataPagamento)}</td>
        <td class="right">${brl(item.valorPago)}</td>
        <td class="center"><span class="status status-${esc(item.status?.toLowerCase())}">${esc(item.status)}</span></td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Contas a Pagar – Detalhado</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #000; background: #fff; }
    @page {
      size: A4 landscape;
      margin: 12mm 10mm 16mm 10mm;
      @bottom-left   { content: "Relatório de Contas a Pagar – Detalhado"; font-size: 7pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${this.dataGeracao}"; font-size: 7pt; color: #555; }
    }
    .header { text-align: center; margin-bottom: 8px; }
    .header h1 { font-size: 14pt; font-weight: 700; }
    .header .meta { font-size: 8pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 6px; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 5px 4px; font-size: 8pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 3px 4px; font-size: 8pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr.even { background-color: #f5f5f5; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tfoot tr td { padding: 4px; font-size: 8pt; font-weight: 700; border-top: 2px solid #000; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .center { text-align: center; }
    .right  { text-align: right; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }
    .status { display: inline-block; padding: 1px 5px; border-radius: 3px; font-size: 7pt; font-weight: 600; border: 1px solid #555; }
    .status-pago       { background-color: #d1e7dd; color: #0a3622; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-aguardando { background-color: #fff3cd; color: #4a3800; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-vencido    { background-color: #f8d7da; color: #721c24; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-cancelado  { background-color: #e2e3e5; color: #383d41; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .summary { margin-top: 10px; font-size: 8pt; color: #333; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Contas a Pagar – Detalhado</h1>
    <div class="meta">
      Tipo: ${tipoLabel} &nbsp;|&nbsp; Período: ${periodLabel} &nbsp;|&nbsp;
      Grupo: ${esc(this.relatorio.grupo || 'Todos')} &nbsp;|&nbsp;
      Subgrupo: ${esc(this.relatorio.subgrupo || 'Todos')} &nbsp;|&nbsp;
      Fornecedor: ${esc(this.relatorio.fornecedor || 'Todos')} &nbsp;|&nbsp;
      Total: ${this.relatorio.totalRegistros} registro(s) &nbsp;|&nbsp; Gerado em: ${this.dataGeracao}
    </div>
    <hr class="divider"/>
  </div>
  <table>
    <thead>
      <tr>
        <th>Id</th>
        <th>Fornecedor</th>
        <th>Grupo</th>
        <th>Subgrupo</th>
        <th>Descrição</th>
        <th>Destino</th>
        <th class="center">Dt. Pagamento</th>
        <th class="right">Vlr. Pago</th>
        <th class="center">Status</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="7" class="right">Totais:</td>
        <td class="right">${brl(this.totalValorPago)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <div class="summary">
    Total pago: <strong>${brl(this.totalValorPago)}</strong>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1200,height=800');
    if (!win) {
      this.toastr.warning('Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }

  private formatDate(value: string | undefined): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? value;
  }
}
